/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-computed-property-dependencies, getter-return, warp-drive/no-legacy-request-patterns */
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { computed } from '@ember/object';
import { later } from '@ember/runloop';
import { isEmpty } from '@ember/utils';
import Options from '@linc-technologies/linced-common/enums/options';
import SchoolStatus from '@linc-technologies/linced-common/enums/school-status';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import moment from 'moment';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import { observes } from '@ember-decorators/object';

export default class DatesService extends Service {
  // Services
  @service
  options;

  @service
  session;

  @service
  store;

  @optionAlias(Options.HERO_ROLLOVER_MONTH)
  _rolloverMonth;
  @optionAlias(Options.HERO_ROLLOVER_DATE)
  _rolloverDate;

  _rolloverDateForYear(date) {
    // Guard against options not being loaded yet — _rolloverMonth/_rolloverDate
    // may be undefined during school switches before options finish loading.
    if (!this._rolloverMonth || !this._rolloverDate) {
      return moment(date);
    }
    let currentYear = moment(date).year();
    return moment(
      `${currentYear}-${this._rolloverMonth}-${this._rolloverDate}`,
    );
  }

  @computed('_rolloverMonth', '_rolloverDate')
  get rolloverMonthDate() {
    return `${this._rolloverMonth}-${this._rolloverDate}`;
  }

  @computed('_rolloverMonth', '_rolloverDate')
  get rolloverDate() {
    let rollover = this._rolloverDateForYear();
    if (rollover.isAfter(moment())) {
      rollover.subtract(1, 'year');
    }
    return rollover.format('YYYY-MM-DD');
  }

  previousRolloverDate(date) {
    let rollover = this._rolloverDateForYear(date);
    if (rollover.isAfter(moment(date))) {
      rollover.subtract(1, 'year');
    }
    return rollover.format('YYYY-MM-DD');
  }

  nextRolloverDate(date) {
    let rollover = this._rolloverDateForYear(date);
    if (rollover.isSameOrBefore(moment(date))) {
      rollover.add(1, 'year');
    }
    return rollover.format('YYYY-MM-DD');
  }

  halfyearDatesForAcademicYear(academicYear) {
    if (academicYear.includes('-')) {
      academicYear = academicYear.split('-')[1];
    }
    // Note: If academic year is 2020 and rollover date is 01-01 this will return 2020-01-01 :thumbsup:
    let rolloverDate = this.previousRolloverDate(`${academicYear}-01-01`);
    return {
      startDate: moment(rolloverDate),
      middleDate: moment(rolloverDate).add(6, 'months'),
      endDate: moment(rolloverDate).add(1, 'year').subtract(1, 'day'),
    };
  }

  @computed('rolloverDate')
  get currentAcademicYear() {
    return moment(this.rolloverDate).year();
  }

  _academicYearLabel(offset) {
    let start = moment(this.rolloverDate);
    if (start.month() === 0 && start.date() === 1) {
      // Starts on January (the 0th month, according to Moment) 1st.
      return `${start.year() + offset}`;
    }
    return `${start.year() + offset}-${start.year() + offset + 1}`;
  }

  @computed('rolloverDate')
  get currentAcademicYearLabel() {
    return this._academicYearLabel(0);
  }

  @computed('rolloverDate')
  get previousAcademicYearLabel() {
    return this._academicYearLabel(-1);
  }

  @computed('rolloverDate')
  get previousPreviousAcademicYearLabel() {
    return this._academicYearLabel(-2);
  }

  @computed('isoWeekday', 'currentTimetable.days.@each.periods')
  get currentTimetableDay() {
    let timetable = this.currentTimetable;
    if (timetable) {
      return findBy(timetable.get('days'), 'isoDayNumber', this.isoWeekday);
    }
  }

  @computed('isoWeekday', 'currentTimetable.days.@each.periods', 'currentTime')
  get currentPeriod() {
    let day = this.currentTimetableDay;
    if (day) {
      let period = null;
      let now = moment();
      let periods = day.get('periods');
      if (periods) {
        periods.forEach(function (p) {
          if (
            now.isSameOrAfter(moment(p.get('startTime'), 'HH:mm')) &&
            now.isBefore(moment(p.get('endTime'), 'HH:mm'))
          ) {
            period = p;
          }
        });
      }
      return period;
    }
  }

  @computed(
    'calendar.@each.{status,week,term}',
    'currentDate',
    'currentAcademicYear',
  )
  get today() {
    return this.getDate(this.currentDate);
  }

  @computed('currentTime', 'today.status')
  get isOpen() {
    switch (this.get('today.status')) {
      case SchoolStatus.OPEN:
        return true;
      case SchoolStatus.MORNING:
        return moment().format('a') === 'am';
      case SchoolStatus.AFTERNOON:
        return moment().format('a') === 'pm';
      default:
        return false;
    }
  }

  structures = [];

  @tracked
  calendar;

  @tracked
  currentTime;

  @tracked
  currentTimetable;

  @tracked
  isoWeekday;

  init() {
    super.init(...arguments);
    this._updateDateAndTime();
    this.options
      .waitUntilLoaded()
      .then(() => {
        return this.updateCalendar(true);
      })
      .then(() => {
        return this.updateTimetable();
      });
  }

  @observes('session.activeSchool', 'currentDate', 'structures')
  _updateTimetable() {
    this.updateTimetable();
  }

  updateTimetable() {
    let date = this.currentDate;
    if (date) {
      this.currentTimetable = this.structureForDate(date);
    }
  }

  @observes('session.activeSchool', 'currentDate')
  _updateCalendar() {
    this.updateCalendar();
  }

  updateCalendar(peek) {
    let year = moment().format('YYYY');
    if (!isEmpty(this.currentAcademicYear)) {
      year = this.currentAcademicYear;
    }
    return this.loadYear(year, peek);
  }

  loadYear(year, peek) {
    let store = this.store;
    let schoolId = this.get('session.activeSchool');
    let { from, to } = this.getYearBounds(year);
    let datesPromise = store.query('date', { schoolId, from, to }).then(() => {
      // Populate `calendar` with all dates we have locally,
      // for navigating between calendar years.
      this.calendar = store.peekAll('date');
    });
    let structuresPromise = Promise.resolve(
      store.peekAll('timetable/structure'),
    );
    if (!peek) {
      structuresPromise = store.query('timetable/structure', {
        schoolId,
        include: 'periods',
      });
    }
    structuresPromise = structuresPromise.then(async (structures) => {
      await this.warmPeriods(structures);
      this.set('structures', structures);
      this.currentTimetable = this.structureForDate(this.currentDate);
      return structures;
    });
    return hash({
      structuresPromise,
      datesPromise,
    });
  }

  // warmPeriods loads each period that a day of a structure refers to and
  // that is not in the store. A day's `periods` only reads loaded records, so
  // load them before the structures are shown.
  warmPeriods(structures) {
    const store = this.store;
    const promises = [];
    for (const structure of structures ?? []) {
      for (const day of structure.days ?? []) {
        for (const id of day.periodIds ?? []) {
          if (!store.peekRecord('timetable/period', id)) {
            // One period that does not load must not stop the others.
            promises.push(
              store.findRecord('timetable/period', id).catch(() => {}),
            );
          }
        }
      }
    }
    return Promise.all(promises);
  }

  _updateDateAndTime() {
    let now = moment();
    this.set('currentDate', now.format('YYYY-MM-DD'));
    this.currentTime = now.format('HH:mm');
    // Assign only a new weekday. Each assignment of a tracked field
    // invalidates currentTimetableDay and its observers, also for the same
    // value. Classic set() did not (regression from HERO-8232).
    let isoWeekday = parseInt(now.format('E'), 10);
    if (this.isoWeekday !== isoWeekday) {
      this.isoWeekday = isoWeekday;
    }

    // Schedule an update just after the minute ticks over.
    let seconds = parseInt(now.format('s'), 10);
    later(this, '_updateDateAndTime', (60 - seconds + 1) * 1000);
  }

  structureForDate(date) {
    let structures = this.structures;

    // Filter out any future structures and sort by effective date
    structures = structures
      .filter(function (structure) {
        return structure.get('effectiveFrom') <= date;
      })
      .sort(
        (a, b) =>
          (a.effectiveFrom || '').localeCompare(b.effectiveFrom || '') ||
          a.createTime - b.createTime,
      );

    // Return the last record, as it will be the active structure for the requested date
    return structures.at(-1);
  }
  dayForDate(date) {
    let structure = this.structureForDate(date);
    if (structure) {
      let day = parseInt(moment(date).format('E'), 10);
      return findBy(structure.get('days'), 'isoDayNumber', day);
    }
  }

  getDate(date) {
    let calendar = this.calendar;
    if (calendar) {
      return calendar.find((d) => d.date === date);
    }
  }

  getYearBounds(year) {
    // Ensure that provided year is numeric.
    year = parseInt(year, 10);
    let from = moment(this.rolloverDate).year(year).format('YYYY-MM-DD');
    let to = moment(this.rolloverDate)
      .year(year + 1)
      .subtract(1, 'day')
      .format('YYYY-MM-DD');

    return { from, to };
  }

  // findClosestTimeframe returns the closest timeframe to today
  findClosestTimeframe(timeframes) {
    if (timeframes.length === 0) {
      return null;
    }
    let now = moment();
    let closestTimeframe = timeframes.at(-1);
    let closestDiff = Math.abs(now.unix() - closestTimeframe.startDateUnix);

    timeframes.forEach((timeframe) => {
      const diff = Math.abs(now.unix() - timeframe.get('startDateUnix'));

      if (diff < closestDiff) {
        closestTimeframe = timeframe;
        closestDiff = diff;
      }
    });
    return closestTimeframe;
  }

  nextSchoolDay(endDate) {
    if (isEmpty(endDate)) {
      return '';
    }
    let date = moment(endDate, 'YYYY-MM-DD');
    let schoolDay = false;
    while (!schoolDay) {
      date.add(1, 'days');

      const heroDate = this.getDate(date.format('YYYY-MM-DD'));

      if (isEmpty(heroDate)) {
        return '';
      }

      // If school date status not closed and is truthy, valid school day
      if (heroDate.status !== SchoolStatus.CLOSED && heroDate.status) {
        schoolDay = true;
      }
    }

    return date;
  }
}
